import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Monitor, 
  Smartphone, 
  Tablet, 
  LogOut, 
  Shield,
  RefreshCw,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ActiveSessionsSection = ({ onLogout }) => {
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(null);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const sessionId = localStorage.getItem('session_id') || '';
      
      const response = await fetch(`${API}/sessions/active`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Session-ID': sessionId
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Mark current session
        const storedId = localStorage.getItem('session_id');
        const sessionsWithCurrent = data.sessions.map(s => ({
          ...s,
          is_current: s.id === storedId || s.is_current
        }));
        setSessions(sessionsWithCurrent);
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const handleLogoutSession = async (sessionId) => {
    const storedSessionId = localStorage.getItem('session_id');
    
    // If logging out current session, do full logout
    if (sessionId === storedSessionId) {
      if (onLogout) onLogout();
      return;
    }

    try {
      setLoggingOut(sessionId);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API}/sessions/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ session_id: sessionId })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success('Session ended successfully');
        // Re-fetch sessions
        fetchSessions();
      } else {
        toast.error(result.detail || result.message || 'Failed to end session');
      }
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to end session');
    } finally {
      setLoggingOut(null);
    }
  };

  const handleLogoutAllOthers = async () => {
    const storedSessionId = localStorage.getItem('session_id');
    
    if (!storedSessionId) {
      toast.error('Cannot identify current session. Please re-login.');
      return;
    }

    try {
      setLoggingOut('all');
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API}/sessions/logout-others`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Session-ID': storedSessionId
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.logged_out_count > 0) {
          toast.success(`Logged out ${data.logged_out_count} other session(s)`);
        } else {
          toast.info('No other sessions to logout');
        }
        fetchSessions();
      } else {
        toast.error('Failed to logout other sessions');
      }
    } catch (error) {
      toast.error('Failed to logout other sessions');
    } finally {
      setLoggingOut(null);
    }
  };

  const handleLogoutEverywhere = async () => {
    if (!window.confirm('This will log you out from ALL devices including this one. You will need to login again. Continue?')) {
      return;
    }

    try {
      setLoggingOut('everywhere');
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API}/sessions/logout-everywhere`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Logged out from all devices. Please login again.');
        // Clear local storage and redirect to login
        setTimeout(() => {
          if (onLogout) onLogout();
        }, 1500);
      } else {
        toast.error('Failed to logout everywhere');
      }
    } catch (error) {
      toast.error('Failed to logout everywhere');
    } finally {
      setLoggingOut(null);
    }
  };

  const getDeviceIcon = (deviceName) => {
    const name = deviceName?.toLowerCase() || '';
    if (name.includes('iphone') || name.includes('android phone')) {
      return <Smartphone className="w-4 h-4" />;
    }
    if (name.includes('ipad') || name.includes('tablet')) {
      return <Tablet className="w-4 h-4" />;
    }
    return <Monitor className="w-4 h-4" />;
  };

  const formatTimeAgo = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Shield className="w-5 h-5" />
            Active Sessions
          </CardTitle>
          <div className="flex items-center gap-2">
            {stats && (
              <Badge variant="outline" className="text-xs">
                {stats.active_sessions}/{stats.max_sessions} devices
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchSessions}
              disabled={loading}
              className="h-7 w-7 p-0"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Sessions expire after 12 hours. Max {stats?.max_sessions || 3} devices allowed.
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="flex justify-center py-4">
            <div className="w-6 h-6 border-2 border-teal-200 border-t-teal-600 rounded-full animate-spin" />
          </div>
        ) : sessions.length === 0 ? (
          <p className="text-gray-500 text-center py-4 text-sm">No active sessions</p>
        ) : (
          <div className="space-y-2">
            {sessions.map((session) => (
              <div
                key={session.id}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  session.is_current 
                    ? 'border-teal-300 bg-teal-50' 
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${
                    session.is_current ? 'bg-teal-200' : 'bg-gray-200'
                  }`}>
                    {getDeviceIcon(session.device_name)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">
                        {session.device_name || 'Unknown Device'}
                      </span>
                      {session.is_current && (
                        <Badge className="bg-teal-600 text-white text-xs px-1.5 py-0">
                          <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />
                          Current
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      {formatTimeAgo(session.last_active_at)}
                      {session.ip_address && ` • ${session.ip_address}`}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`text-xs h-7 ${
                    session.is_current 
                      ? 'text-red-600 hover:text-red-700 hover:bg-red-50' 
                      : 'text-gray-600 hover:text-red-600 hover:bg-red-50'
                  }`}
                  onClick={() => handleLogoutSession(session.id)}
                  disabled={loggingOut === session.id || loggingOut === 'all'}
                >
                  <LogOut className="w-3 h-3 mr-1" />
                  {loggingOut === session.id ? '...' : session.is_current ? 'Logout' : 'End'}
                </Button>
              </div>
            ))}
            
            {/* Logout All Others Button */}
            {sessions.length > 1 && (
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-3 text-red-600 border-red-200 hover:bg-red-50"
                onClick={handleLogoutAllOthers}
                disabled={loggingOut === 'all' || loggingOut === 'everywhere'}
              >
                <LogOut className="w-3 h-3 mr-2" />
                {loggingOut === 'all' ? 'Logging out...' : 'Logout All Other Devices'}
              </Button>
            )}

            {/* Security Warning & Logout Everywhere */}
            <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-800 mb-2">
                <strong>See an unrecognized device?</strong> Change your password immediately - this will also log you out from all devices.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="w-full text-amber-700 border-amber-300 hover:bg-amber-100"
                onClick={handleLogoutEverywhere}
                disabled={loggingOut === 'everywhere'}
              >
                <Shield className="w-3 h-3 mr-2" />
                {loggingOut === 'everywhere' ? 'Logging out...' : 'Logout From Everywhere'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActiveSessionsSection;
